import type { ReactNode } from 'react';

/**
 * Shared icon set (D-096): outline glyphs on a 20×20 grid, drawn with the
 * current text colour, matching the sidebar icons. Inline SVG rather than an
 * icon package: the set is small, and the strokes stay consistent with the
 * hand-drawn navigation icons.
 */

const ICON_PATHS = {
  keyword: <path d="M7.5 3.5 6 16.5M14 3.5l-1.5 13M3.5 7.5h13M3 12.5h13" />,
  message: <path d="M4 5.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9.5L6 16.5v-3a2 2 0 0 1-2-2Z" />,
  comment: (
    <>
      <path d="M3.5 6a2.5 2.5 0 0 1 2.5-2.5h8A2.5 2.5 0 0 1 16.5 6v5a2.5 2.5 0 0 1-2.5 2.5H8l-3.5 3v-3.2A2.5 2.5 0 0 1 3.5 11Z" />
      <path d="M7 7.5h6M7 10h4" />
    </>
  ),
  story: (
    <>
      <circle cx="10" cy="10" r="6.5" strokeDasharray="3.2 2.2" />
      <circle cx="10" cy="10" r="3" />
    </>
  ),
  mention: (
    <>
      <circle cx="10" cy="10" r="2.6" />
      <path d="M12.6 10v1.1a1.9 1.9 0 0 0 3.8 0V10a6.4 6.4 0 1 0-2.6 5.2" />
    </>
  ),
  live: (
    <>
      <circle cx="10" cy="10" r="1.6" />
      <path d="M7 7a4.2 4.2 0 0 0 0 6M13 7a4.2 4.2 0 0 1 0 6M4.6 4.6a7.6 7.6 0 0 0 0 10.8M15.4 4.6a7.6 7.6 0 0 1 0 10.8" />
    </>
  ),
  contactAdd: (
    <>
      <circle cx="8.5" cy="7" r="3" />
      <path d="M3 16c.6-2.7 2.8-4.5 5.5-4.5 1.2 0 2.3.3 3.2.9M15 11.5v5M12.5 14h5" />
    </>
  ),
  tag: (
    <>
      <path d="M3.5 10.2V4.5a1 1 0 0 1 1-1h5.7l6.3 6.3a1 1 0 0 1 0 1.4l-5.6 5.6a1 1 0 0 1-1.4 0Z" />
      <circle cx="7.2" cy="7.2" r="1" />
    </>
  ),
  tagOff: (
    <>
      <path d="M3.5 10.2V4.5a1 1 0 0 1 1-1h5.7l6.3 6.3a1 1 0 0 1 0 1.4l-5.6 5.6a1 1 0 0 1-1.4 0Z" />
      <path d="M3 3l14 14" />
    </>
  ),
  play: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="m8.5 7.2 4 2.8-4 2.8Z" />
    </>
  ),
  webhook: <path d="M8 12l4-4M6.6 9.4 5 11a2.6 2.6 0 0 0 3.7 3.7l1.5-1.6M13.4 10.6 15 9a2.6 2.6 0 0 0-3.7-3.7L9.8 6.9" />,
  send: <path d="M16.5 3.5 3.5 9l5.2 2.3L11 16.5Zm0 0-7.8 7.8" />,
  reply: <path d="M8 5.5 3.5 10 8 14.5M3.5 10h7.5a5 5 0 0 1 5 5v1" />,
  heart: <path d="M10 16.2s-6.2-3.7-6.2-8.1A3.3 3.3 0 0 1 10 6.3a3.3 3.3 0 0 1 6.2 1.8c0 4.4-6.2 8.1-6.2 8.1Z" />,
  field: (
    <>
      <rect x="3.5" y="4.5" width="13" height="11" rx="2" />
      <path d="M6.5 8.5h7M6.5 11.5h4" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="4.5" width="14" height="11" rx="2" />
      <path d="m3.5 6 6.5 4.8L16.5 6" />
    </>
  ),
  clock: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 6.5V10l2.5 1.6" />
    </>
  ),
  globe: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M3.5 10h13M10 3.5c2.2 2.4 2.2 10.6 0 13M10 3.5c-2.2 2.4-2.2 10.6 0 13" />
    </>
  ),
  jump: <path d="M3.5 10h10M10.5 6.5 14 10l-3.5 3.5M16.5 5v10" />,
  stop: <rect x="5" y="5" width="10" height="10" rx="2" />,
  layers: <path d="M10 3.5 3.5 7 10 10.5 16.5 7ZM3.5 10.5 10 14l6.5-3.5M3.5 13.8 10 17.3l6.5-3.5" />,
  history: <path d="M4 10a6 6 0 1 0 1.8-4.3M4 3.8v2.6h2.6M10 7v3l2 1.5" />,
  follows: (
    <>
      <circle cx="8.5" cy="7" r="3" />
      <path d="M3 16c.6-2.7 2.8-4.5 5.5-4.5 1.2 0 2.3.3 3.2.9M12.5 14.2l1.7 1.7 3.3-3.4" />
    </>
  ),
  search: (
    <>
      <circle cx="9" cy="9" r="5" />
      <path d="m12.6 12.6 3.9 3.9" />
    </>
  ),
  equals: <path d="M5 8h10M5 12h10" />,
  greater: <path d="m7 6 6 4-6 4" />,
  less: <path d="m13 6-6 4 6 4" />,
  menu: <path d="M4 6h12M4 10h12M4 14h12" />,
  pause: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M8.5 7.5v5M11.5 7.5v5" />
    </>
  ),
  sequence: (
    <>
      <circle cx="5" cy="5.5" r="1.5" />
      <circle cx="5" cy="14.5" r="1.5" />
      <path d="M5 7v6M8.5 5.5h8M8.5 14.5h8M8.5 10h5" />
    </>
  ),
  segment: <path d="M3.5 4.5h13l-5 6.2v4.8l-3-1.5v-3.3Z" />,
  gift: (
    <>
      <rect x="3.5" y="8" width="13" height="8.5" rx="1.5" />
      <path d="M2.8 5.5h14.4V8H2.8ZM10 5.5v11M10 5.5C9.2 3.2 6.3 3 6.6 4.7c.2 1 3.4.8 3.4.8ZM10 5.5c.8-2.3 3.7-2.5 3.4-.8-.2 1-3.4.8-3.4.8Z" />
    </>
  ),
  quiz: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M8 8a2 2 0 1 1 2.8 1.8c-.5.3-.8.7-.8 1.3v.4M10 13.6h.01" />
    </>
  ),
  coupon: (
    <>
      <path d="M3.5 6.5a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1V8a2 2 0 0 0 0 4v1.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V12a2 2 0 0 0 0-4Z" />
      <path d="M8 8.5h.01M12 11.5h.01M12.5 8l-5 4" />
    </>
  ),
  link: <path d="M8.5 11.5l3-3M7 9.5 5.5 11a2.5 2.5 0 0 0 3.5 3.5l1.5-1.5M13 10.5l1.5-1.5A2.5 2.5 0 0 0 11 5.5L9.5 7" />,
  users: (
    <>
      <circle cx="7.5" cy="7" r="2.6" />
      <circle cx="13.6" cy="7.8" r="2" />
      <path d="M2.8 15.5c.5-2.4 2.4-4 4.7-4s4.2 1.6 4.7 4M12.4 11.6c2.2-.4 4.2.9 4.8 3.4" />
    </>
  ),
  sparkle: <path d="M10 3.5 11.5 8.5 16.5 10l-5 1.5L10 16.5 8.5 11.5 3.5 10l5-1.5ZM15.5 3v3M14 4.5h3" />,
  dot: <circle cx="10" cy="10" r="2.5" />,

  // Refonte additions: UI chrome (controls, navigation, actions).
  chevronDown: <path d="m5.5 8 4.5 4.5L14.5 8" />,
  chevronUp: <path d="m5.5 12 4.5-4.5 4.5 4.5" />,
  chevronRight: <path d="m8 5.5 4.5 4.5L8 14.5" />,
  chevronLeft: <path d="M12 5.5 7.5 10l4.5 4.5" />,
  chevronsUpDown: <path d="m6.5 7.5 3.5-3.5 3.5 3.5M6.5 12.5l3.5 3.5 3.5-3.5" />,
  check: <path d="m4.5 10.5 3.5 3.5 7.5-8" />,
  x: <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />,
  plus: <path d="M10 4.5v11M4.5 10h11" />,
  alert: (
    <>
      <path d="M8.7 4.2a1.5 1.5 0 0 1 2.6 0l5.6 9.8a1.5 1.5 0 0 1-1.3 2.2H4.4a1.5 1.5 0 0 1-1.3-2.2Z" />
      <path d="M10 8v3.2M10 13.6h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 9.2v4M10 6.6h.01" />
    </>
  ),
  arrowRight: <path d="M4 10h12M11.5 5.5 16 10l-4.5 4.5" />,
  arrowLeft: <path d="M16 10H4M8.5 5.5 4 10l4.5 4.5" />,
  arrowDown: <path d="M10 4v12M5.5 11.5 10 16l4.5-4.5" />,
  more: (
    <>
      <circle cx="4.5" cy="10" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="10" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  grip: (
    <>
      <circle cx="7.5" cy="5.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="5.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="10" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="10" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.5 11.5 6 5h8l2.5 6.5V15a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-3.5Z" />
      <path d="M3.5 11.5H7a3 3 0 0 0 6 0h3.5" />
    </>
  ),
  home: <path d="M3.5 10.5 10 4l6.5 6.5M5 9.5V16h3.5v-3.5h3V16H15V9.5" />,
  workflow: (
    <>
      <rect x="3" y="3" width="5" height="4.5" rx="1.2" />
      <rect x="12" y="12.5" width="5" height="4.5" rx="1.2" />
      <path d="M5.5 7.5v3a2 2 0 0 0 2 2h5M14.5 12.5v-3a2 2 0 0 0-2-2h-1" />
    </>
  ),
  template: (
    <>
      <rect x="3.5" y="3.5" width="13" height="13" rx="1.5" />
      <path d="M3.5 8h13M8 8v8.5" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4.5" width="14" height="12" rx="2" />
      <path d="M3 8.5h14M7 3v3M13 3v3" />
    </>
  ),
  megaphone: (
    <>
      <path d="M4 8.5v3a1 1 0 0 0 1 1h1.5l5 3.5V4L6.5 7.5H5a1 1 0 0 0-1 1Z" />
      <path d="M14.5 7.5a3.5 3.5 0 0 1 0 5" />
    </>
  ),
  chart: <path d="M3.5 3.5v13h13M6.5 12.5l3-3.5 2.5 2 4-4.5" />,
  gauge: <path d="M4 14.5a6 6 0 1 1 12 0M10 14.5l3-4" />,
  share: (
    <>
      <circle cx="5" cy="10" r="2" />
      <circle cx="15" cy="5" r="2" />
      <circle cx="15" cy="15" r="2" />
      <path d="m6.8 9 6.4-3.2M6.8 11l6.4 3.2" />
    </>
  ),
  settings: (
    <>
      <path d="M8.6 3.5h2.8l.4 1.9 1.6.9 1.8-.7 1.4 2.4-1.4 1.3v1.4l1.4 1.3-1.4 2.4-1.8-.7-1.6.9-.4 1.9H8.6l-.4-1.9-1.6-.9-1.8.7-1.4-2.4 1.4-1.3V9.3L3.4 8l1.4-2.4 1.8.7 1.6-.9Z" />
      <circle cx="10" cy="10" r="2.3" />
    </>
  ),
  user: (
    <>
      <circle cx="10" cy="6.5" r="2.75" />
      <path d="M4.5 16.5c.7-3 3-4.5 5.5-4.5s4.8 1.5 5.5 4.5" />
    </>
  ),
  upload: <path d="M10 13V4M6.5 7.5 10 4l3.5 3.5M4 13v2a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 15v-2" />,
  download: <path d="M10 4v9M6.5 9.5 10 13l3.5-3.5M4 13v2a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 15v-2" />,
  trash: <path d="M4 6h12M8 6V4.5h4V6M5.5 6l.7 9.6a1 1 0 0 0 1 .9h5.6a1 1 0 0 0 1-.9L14.5 6M8.5 9v4.5M11.5 9v4.5" />,
  pencil: <path d="m12.5 4.5 3 3L7 16H4v-3ZM11 6l3 3" />,
  receipt: <path d="M5 3.5h10v13l-2-1.3-1.7 1.3-1.3-1.3-1.3 1.3L7 15.2l-2 1.3ZM7.5 7h5M7.5 10h5" />,
  /** A feature the plan does not include (D-183). */
  lock: (
    <>
      <rect x="4.5" y="8.5" width="11" height="8" rx="1.8" />
      <path d="M7 8.5v-2a3 3 0 0 1 6 0v2" />
    </>
  ),
  logout: <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M12.5 13.5 16 10l-3.5-3.5M16 10H8" />,
  circleDot: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <circle cx="10" cy="10" r="1.5" />
    </>
  ),
  checkCircle: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="m7.2 10.2 2 2 3.8-4" />
    </>
  ),
  power: <path d="M10 3.5V10M6 5.8a6 6 0 1 0 8 0" />,
  zap: <path d="M11 3 4.5 11H10l-1 6 6.5-8H10Z" />,
  sliders: <path d="M3.5 6.5h7M14.5 6.5h2M3.5 13.5h2M9.5 13.5h7M12.5 4.5v4M7.5 11.5v4" />,
  minus: <path d="M4.5 10h11" />,
  braces: <path d="M7 4.5c-1.5 0-2 .7-2 2v1.6c0 .9-.5 1.6-1.5 1.9 1 .3 1.5 1 1.5 1.9v1.6c0 1.3.5 2 2 2M13 4.5c1.5 0 2 .7 2 2v1.6c0 .9.5 1.6 1.5 1.9-1 .3-1.5 1-1.5 1.9v1.6c0 1.3-.5 2-2 2" />,
  // Custom field types (refonte Settings): text, number, dropdown, phone.
  textType: <path d="M4.5 6V4.5h11V6M10 4.5v11M8 15.5h4" />,
  hash: <path d="M8.2 3.5 6.8 16.5M13.2 3.5l-1.4 13M4 7.5h12.5M3.5 12.5H16" />,
  list: <path d="M8 6h8M8 10h8M8 14h8M4.2 6h.01M4.2 10h.01M4.2 14h.01" />,
  phone: <path d="M7 3.5H5a1.2 1.2 0 0 0-1.2 1.3C4.4 11 9 15.6 15.2 16.2a1.2 1.2 0 0 0 1.3-1.2v-2l-3-1.4-1.6 1.5c-1.7-.8-3.3-2.4-4.1-4.1L9.3 7.4Z" />,
  cloudCheck: (
    <>
      <path d="M6 15.5a3.5 3.5 0 0 1-.4-7 4.5 4.5 0 0 1 8.7-.9A3.5 3.5 0 0 1 14 15.5Z" />
      <path d="m8 11.5 1.5 1.5 2.8-3" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="14" height="9.5" rx="1.5" />
      <path d="M7.5 16.5h5M10 13.5v3" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3.5" width="14" height="13" rx="2" />
      <circle cx="7.5" cy="8" r="1.3" />
      <path d="m3.5 14.5 4-4 3 3 2-2 4 4" />
    </>
  ),
  spreadsheet: (
    <>
      <rect x="4" y="3" width="12" height="14" rx="1.5" />
      <path d="M4 8h12M4 12.5h12M9 8v9" />
    </>
  ),
  /** A carousel post: one frame with the next ones peeking out. */
  carousel: (
    <>
      <rect x="6.5" y="4" width="7" height="12" rx="1.5" />
      <path d="M3.5 5.5v9M16.5 5.5v9" />
    </>
  ),
  /** A Reel: a clapperboard. */
  reel: (
    <>
      <rect x="3.5" y="8" width="13" height="8.5" rx="1.5" />
      <path d="m3.6 8 .9-3.6 11.5-.1.6 3.7M7.6 4.3 8.8 8M11.8 4.3 13 8" />
    </>
  ),
  refresh: <path d="M15.8 8.5A6 6 0 0 0 5 6.2M4.2 11.5A6 6 0 0 0 15 13.8M15.8 3.8v4.7h-4.7M4.2 16.2v-4.7h4.7" />,
} as const satisfies Record<string, ReactNode>;

export type IconName = keyof typeof ICON_PATHS;

export function Icon({ name, className = 'size-4' }: { name: IconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      data-icon={name}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// --- channel logos ------------------------------------------------------------------

const glyphStroke = { fill: 'none', stroke: 'white', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

interface ChannelMeta {
  name: string;
  /** Tile background: the network's own colours, so the logo reads at a glance. */
  background: string;
  glyph: ReactNode;
}

export const CHANNEL_META: Record<string, ChannelMeta> = {
  instagram: {
    name: 'Instagram',
    background: 'linear-gradient(135deg,#FEDA75,#D62976 55%,#962FBF)',
    glyph: (
      <>
        <rect x="3.5" y="3.5" width="13" height="13" rx="4" />
        <circle cx="10" cy="10" r="3" />
        <circle cx="14.2" cy="5.8" r="0.4" fill="white" stroke="none" />
      </>
    ),
  },
  messenger: {
    name: 'Messenger',
    background: 'linear-gradient(135deg,#0695FF,#A334FA)',
    glyph: (
      <>
        <path d="M10 3.5c-3.9 0-7 2.8-7 6.3 0 2 1 3.7 2.6 4.9V17l2.4-1.3c.6.2 1.3.3 2 .3 3.9 0 7-2.8 7-6.3s-3.1-6.2-7-6.2Z" />
        <path d="m6.5 11.5 2.5-2.5 2 1.5 2.5-2.5" />
      </>
    ),
  },
  mock: {
    name: 'Mock (dev)',
    background: 'linear-gradient(135deg,#8A8FA3,#5B6075)',
    glyph: <path d="M4 5.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9.5L6 16.5v-3a2 2 0 0 1-2-2Z" />,
  },
};

export function channelName(code: string): string {
  return CHANNEL_META[code]?.name ?? code;
}

/** A channel's logo tile. Unknown codes render a neutral tile with the code's initial. */
export function ChannelLogo({ code, size = 16, title }: { code: string; size?: number; title?: string }) {
  const meta = CHANNEL_META[code];
  const label = title ?? channelName(code);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex shrink-0 items-center justify-center rounded-[28%]"
      style={{ width: size, height: size, background: meta?.background ?? 'var(--color-ink-300)' }}
    >
      {meta ? (
        <svg viewBox="0 0 20 20" width={Math.round(size * 0.72)} height={Math.round(size * 0.72)} aria-hidden {...glyphStroke}>
          {meta.glyph}
        </svg>
      ) : (
        <span className="text-[9px] font-bold uppercase text-white">{code.slice(0, 1)}</span>
      )}
    </span>
  );
}
